import React from 'react';

export const formatCurrency = (val) => {
  if (val == null) return '\u00A30.00';
  return `\u00A3${Number(val).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Accounting-style: green for positive, red in brackets for negative
export const formatAccounting = (val, opts = {}) => {
  if (val == null) return <span className="tabular-nums">{'\u00A3'}0.00</span>;
  const num = Number(val);
  const abs = Math.abs(num).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (num < 0) {
    return <span className={`tabular-nums ${opts.className || ''} text-red-600 font-semibold`}>({'\u00A3'}{abs})</span>;
  }
  if (num > 0) {
    return <span className={`tabular-nums ${opts.className || ''} text-emerald-600 font-semibold`}>{'\u00A3'}{abs}</span>;
  }
  return <span className={`tabular-nums ${opts.className || ''}`}>{'\u00A3'}0.00</span>;
};

// Quantity accounting-style: no currency sign, green for positive, red in brackets for negative
export const formatQtyAccounting = (val, opts = {}) => {
  if (val == null) return <span className="tabular-nums">0.0000</span>;
  const num = Number(val);
  const abs = Math.abs(num).toLocaleString('en-GB', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  if (num < 0) {
    return <span className={`tabular-nums ${opts.className || ''} text-red-600 font-semibold`}>({abs})</span>;
  }
  if (num > 0) {
    return <span className={`tabular-nums ${opts.className || ''} text-emerald-600 font-semibold`}>{abs}</span>;
  }
  return <span className={`tabular-nums ${opts.className || ''}`}>0.0000</span>;
};

// iter390 — Plain-text accounting formatter for use INSIDE template literals
// (string interpolation). The JSX `formatAccounting` returns a React element
// which gets coerced to "[object Object]" when concatenated as a string.
// Use this in `example` strings, debug captions, console output, etc.
export const formatAccountingText = (val) => {
  if (val == null) return '\u00A30.00';
  const num = Number(val);
  const abs = Math.abs(num).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (num < 0) return `(\u00A3${abs})`;
  return `\u00A3${abs}`;
};

// Pre-deployment defect sweep #3 — GP% rounding parity between the
// live wizard UI and the PDF reports. JS's `Number.prototype.toFixed`
// rounds based on the RAW binary double, which for boundary values
// like 79.45 can silently round DOWN (79.4) even though the intended
// decimal value should round half-up to 79.5 — exactly the discrepancy
// seen vs the PDF (Python `Decimal(str(value))` rounding, which reads
// the literal decimal digits, not the binary noise). This mirrors that
// same "read the decimal string, round half-up" approach in JS so both
// surfaces agree on the same canonical resolved value's display.
export const roundHalfUp = (value, decimals = 1) => {
  if (value == null || Number.isNaN(Number(value))) return value;
  const num = Number(value);
  const negative = num < 0;
  const str = Math.abs(num).toString();
  const [intPart, fracPart = ''] = str.split('.');
  const paddedFrac = fracPart.padEnd(decimals + 1, '0');
  const keepDigits = paddedFrac.slice(0, decimals);
  const nextDigit = paddedFrac.charCodeAt(decimals) - 48; // '0' => 48
  let shifted = BigInt(intPart + keepDigits || '0');
  if (nextDigit >= 5) shifted += 1n;
  const divisor = 10 ** decimals;
  const rounded = Number(shifted) / divisor;
  return negative ? -rounded : rounded;
};

// Data-source diagnostic badge
export const DataSourceBadge = ({ source }) => {
  const config = {
    'package': { label: 'Package/IndexedDB', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
    'local-engine': { label: 'Local Engine', cls: 'bg-purple-100 text-purple-700 border-purple-200' },
    'live-api': { label: 'Live API', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  };
  const c = config[source] || config['live-api'];
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono border ${c.cls}`} data-testid={`data-source-${source}`}>
      {c.label}
    </span>
  );
};
